// src/components/navigation/tier-indicator.tsx
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Crown, Zap, Sparkles, Users, Building2, Info } from 'lucide-react'


import type { SubscriptionTier } from '@/types'

interface TierIndicatorProps {
  tier: SubscriptionTier
  usage?: {
    current: number
    limit: number | null
  }
  cost?: number
  className?: string
}

export function TierIndicator({ 
  tier, 
  usage = { current: 0, limit: null }, 
  cost,
  className 
}: TierIndicatorProps) {
  const getTierConfig = (tier: SubscriptionTier) => {
    const configs = {
  free: {
    label: 'Free',
    icon: Sparkles,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    hoverColor: 'hover:bg-gray-200',
  },
  starter: {
    label: 'Starter',
    icon: Zap,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    hoverColor: 'hover:bg-green-200',
  },
  pro: {
    label: 'Pro',
    icon: Crown,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    hoverColor: 'hover:bg-blue-200',
  },
  team: {
    label: 'Team',
    icon: Users,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    hoverColor: 'hover:bg-orange-200',
  },
  enterprise: {
    label: 'Enterprise',
    icon: Building2,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    hoverColor: 'hover:bg-purple-200',
  },
}

    return configs[tier] || configs.free
  }

  const config = getTierConfig(tier)
  const Icon = config.icon

  const formatUsage = () => {
    if (tier === 'enterprise' && cost) {
      return `$${cost.toFixed(2)}`
    }
    
    if (usage.limit === null) {
      return `${usage.current}/∞`
    }
    
    return `${usage.current}/${usage.limit}`
  }

  const getUsageColor = () => {
    if (tier === 'enterprise') return ''
    
    if (!usage.limit) return ''
    
    const percentage = usage.current / usage.limit
    if (percentage >= 0.9) return 'text-red-600'
    if (percentage >= 0.7) return 'text-orange-600'
    return ''
  }

  return (
    <Link
      to="/billing"
      className={cn(
        'flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
        config.bgColor,
        config.color,
        config.hoverColor,
        className
      )}
      title={`View billing details for ${config.label} plan`}
    >
      <Icon className="w-4 h-4" />
      <span>{config.label}</span>
      <span className={cn('text-xs', getUsageColor())}>
        ({formatUsage()})
      </span>
      <Info className="w-3 h-3 opacity-50" />
    </Link>
  )
}